// metaMarksPlugin.js
// Plugin for styling and highlighting overlapping meta marks

import {Plugin, PluginKey, TextSelection} from "prosemirror-state";

export const metaMarksPluginKey = new PluginKey("metaMarks");

function sameUidSet(left, right) {
    if (left === right) return true;
    if (!left || !right) return false;
    if (left.size !== right.size) return false;
    for (const value of left) {
        if (!right.has(value)) return false;
    }
    return true;
}

/**
 * Find the starting and ending positions of a mark in the document.
 * Uses a pre-built positions cache when available to avoid a full scan.
 * @param {Node} doc
 * @param {string} uid
 * @param {Map<string, {from: number, to: number}>|null} posCache - optional uid→range cache
 * @returns {{from: number, to: number}}
 */
function findMarkPositions(doc, uid, posCache = null) {
    if (posCache && posCache.has(uid)) return posCache.get(uid);

    let startPos = -1;
    let endPos = -1;
    doc.descendants((node, pos) => {
        if (!node.isText) return;
        for (const mark of node.marks) {
            const markId = mark.attrs.uid || mark.attrs.id;
            if (markId === uid) {
                if (startPos === -1) startPos = pos;
                endPos = pos + node.nodeSize;
            }
        }
    });
    return {from: startPos, to: endPos};
}

/**
 * Build a uid→{from, to} cache by scanning the document once.
 * Only meta-mark UIDs are indexed.
 */
function buildMarkPosCache(doc) {
    const cache = new Map();
    doc.descendants((node, pos) => {
        if (!node.isText) return;
        for (const mark of node.marks) {
            const uid = mark.attrs.uid || mark.attrs.id;
            if (!uid) continue;
            const entry = cache.get(uid);
            if (!entry) {
                cache.set(uid, {from: pos, to: pos + node.nodeSize});
            } else {
                if (pos < entry.from) entry.from = pos;
                const end = pos + node.nodeSize;
                if (end > entry.to) entry.to = end;
            }
        }
    });
    return cache;
}

/**
 * Get the mark with highest starting position at the current selection position
 * For overlapping marks, only the innermost (highest start pos) mark is considered active
 * When multiple marks have the same start position, the one with lowest end position is selected
 * @param {EditorState} state - ProseMirror editor state
 * @param {Map<string, {from: number, to: number}>|null} posCache - optional pre-built uid→range cache
 * @returns {Set<string>} Set containing at most one UID - the mark with highest start position (and lowest end if tied)
 */
function getActiveMarkUids(state, posCache = null) {
    const activeUids = new Set();
    const {selection} = state;

    if (!(selection instanceof TextSelection)) {
        return activeUids;
    }

    const {$from, empty} = selection;

    // For collapsed selection (caret), get marks at the cursor position
    if (empty) {
        const marks = state.storedMarks || $from.marks();
        const boundaryMarks = [];
        if ($from.parentOffset > 0 && $from.nodeBefore?.marks) {
            boundaryMarks.push(...$from.nodeBefore.marks);
        }
        if ($from.nodeAfter?.marks) {
            boundaryMarks.push(...$from.nodeAfter.marks);
        }
        const allMarks = [...marks, ...boundaryMarks];

        // Fast path: no marks at cursor at all
        if (allMarks.length === 0) return activeUids;

        // Collect all mark UIDs with their start and end positions
        const candidates = [];
        allMarks.forEach((mark) => {
            // Handle both 'uid' (for comment/reference/task) and 'id' (for wikidataEntity)
            const markId = mark.attrs.uid || mark.attrs.id;
            if (markId) {
                const {from, to} = findMarkPositions(state.doc, markId, posCache);
                if (from !== -1) {
                    candidates.push({uid: markId, from: from, to: to});
                }
            }
        });

        // Sort by start position (descending), then by end position (ascending) for ties
        // This selects the mark with highest start, and if multiple marks start at same position,
        // selects the one with lowest end (shortest/most specific mark)
        if (candidates.length > 0) {
            candidates.sort((a, b) => {
                if (b.from !== a.from) {
                    return b.from - a.from; // Higher start position first
                }
                return a.to - b.to; // Lower end position first (shorter mark)
            });
            activeUids.add(candidates[0].uid);
        }
    } else {
        // For range selection, get marks at the start of the selection
        // and apply the same logic
        const marks = $from.marks();

        // Fast path: no marks at selection start
        if (marks.length === 0) return activeUids;

        const candidates = [];
        marks.forEach((mark) => {
            const markId = mark.attrs.uid || mark.attrs.id;
            if (markId) {
                const {from, to} = findMarkPositions(state.doc, markId, posCache);
                if (from !== -1) {
                    candidates.push({uid: markId, from: from, to: to});
                }
            }
        });

        if (candidates.length > 0) {
            candidates.sort((a, b) => {
                if (b.from !== a.from) {
                    return b.from - a.from; // Higher start position first
                }
                return a.to - b.to; // Lower end position first (shorter mark)
            });
            activeUids.add(candidates[0].uid);
        }
    }

    return activeUids;
}

/**
 * Toggle the "active" CSS class directly on schema-mark DOM elements
 * (e.g. <span data-reference-uid>, <span data-comment-uid>, etc.)
 * instead of using Decoration.inline, which fragments across text-node
 * boundaries and creates many small wrapper <span>s.
 *
 */
function syncActiveClassToDOM(editorDOM, newActiveUids) {

    // Attribute selectors that the schema marks produce via toDOM
    const selectors = [
        "span[data-reference-uid]",
        "span[data-comment-uid]",
        "span[data-task-uid]",
        "span[data-wikidata-uid]",
    ];
    const attrNames = [
        "data-reference-uid",
        "data-comment-uid",
        "data-task-uid",
        "data-wikidata-uid",
    ];

    for (let i = 0; i < selectors.length; i++) {
        const elements = editorDOM.querySelectorAll(selectors[i]);
        for (const el of elements) {
            const uid = el.getAttribute(attrNames[i]);
            if (newActiveUids.has(uid)) {
                if (!el.classList.contains("active")) {
                    el.classList.add("active");
                }
            } else {
                el.classList.remove("active");
            }
        }
    }
}

function runWithDomObserverSuspended(view, callback) {
    if (!view || typeof callback !== "function") return;

    const domObserver = view.domObserver;
    const canSuspend = domObserver
        && typeof domObserver.stop === "function"
        && typeof domObserver.start === "function";

    if (!canSuspend) {
        callback();
        return;
    }

    domObserver.stop();
    try {
        callback();
    } finally {
        domObserver.start();
    }
}

/**
 * Create the meta marks plugin
 * @param {Object} options - Plugin options
 * @returns {Plugin} ProseMirror plugin
 */
export function metaMarksPlugin() {
    return new Plugin({
        key: metaMarksPluginKey,

        state: {
            init(_, {doc, schema, selection}) {
                return getActiveMarkUids({selection, schema, doc, storedMarks: null});
            },
            apply(tr, prevActiveUids, oldState, newState) {
                const docChanged = tr.docChanged;
                const selectionChanged = !oldState.selection.eq(newState.selection);

                if (docChanged || selectionChanged) {
                    // Build the position cache once per apply when the doc changed;
                    // for selection-only moves the cache is built on demand inside
                    // findMarkPositions (null → falls back to per-uid scan, but that is
                    // cheap when there are few marks at the cursor).
                    // ponytail: cache is O(n) to build — only paid on docChanged.
                    const posCache = docChanged ? buildMarkPosCache(newState.doc) : null;
                    const nextActiveUids = getActiveMarkUids(newState, posCache);
                    return sameUidSet(prevActiveUids, nextActiveUids) ? prevActiveUids : nextActiveUids;
                }

                return prevActiveUids;
            },
        },

        view(editorView) {
            let rafId = null;

            function scheduleSync(view) {
                if (rafId) cancelAnimationFrame(rafId);
                rafId = requestAnimationFrame(() => {
                    rafId = null;
                    const activeUids = metaMarksPluginKey.getState(view.state);
                    if (activeUids) {
                        runWithDomObserverSuspended(view, () => {
                            syncActiveClassToDOM(view.dom, activeUids);
                        });
                    }
                });
            }

            // Initial sync
            scheduleSync(editorView);

            return {
                update(view, prevState) {
                    const activeUids = metaMarksPluginKey.getState(view.state);
                    const prevActiveUids = metaMarksPluginKey.getState(prevState);

                    // Only touch the DOM when the active set actually changed
                    if (activeUids !== prevActiveUids) {
                        scheduleSync(view);
                    }
                },
                destroy() {
                    if (rafId) cancelAnimationFrame(rafId);
                },
            };
        },
    });
}

/**
 * Get meta marks plugin state
 */
export function getMetaMarksPluginState(state) {
    return metaMarksPluginKey.getState(state);
}

/**
 * Get all marks that are currently active (at caret/selection)
 * @param {EditorState} state - ProseMirror editor state
 * @returns {Set<string>} Set of UIDs for marks at the caret/selection
 */
export function getActiveMarks(state) {
    return getActiveMarkUids(state, null);
}
